import * as React from 'react';

/**
 * Viewport width (px) below which we treat the UI as "mobile". Matches
 * Tailwind's `md` breakpoint so JS-driven layout decisions stay in sync with
 * the `md:` utility classes used in markup.
 */
export const MOBILE_BREAKPOINT = 768;

const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`;

/**
 * Reactive boolean that flips when the viewport crosses the mobile breakpoint.
 *
 * Driven entirely by `window.matchMedia` (not `innerWidth`) so it stays
 * deterministic under test — the matchMedia mock in `test/matchMedia.ts` can
 * drive it via `setViewportWidth()`.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = React.useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY).matches : false,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mql.addEventListener('change', onChange);
    // Re-read in case the width changed between the initial render and effect.
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}
